import { useState } from "react";
import { Input, InputNumber, Button, Tooltip } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import { getEditableTranslatableValue } from "./translatable.util";
import { IconPickerField } from "./IconPickerField";
import { AsyncSelectField } from "./AsyncSelectField";

// Formats a calorie bound with comma-thousands-separators, matching the
// existing seeded answer style ("1,200", "2,800").
function formatCalorie(n) {
    return Number(n).toLocaleString("en-US");
}

// Builds the calorie-range answer label from Min/Max, matching the exact
// wording already used in the seeded quiz answers — "Under X kcal" / "X+
// kcal" for open-ended bounds, "X – Y kcal" (en dash) when both are set.
function formatCalorieRangeLabel(min, max) {
    if (min != null && max != null) return `${formatCalorie(min)} – ${formatCalorie(max)} kcal`;
    if (max != null) return `Under ${formatCalorie(max)} kcal`;
    if (min != null) return `${formatCalorie(min)}+ kcal`;
    return "";
}

// Compact Min/Max calorie inputs + confirm control for the calorie
// question's answers (QUIZ_CALORIE_ANSWER_SUB_FIELDS). Typing only updates
// local pending state — nothing is written to the item, and the label is
// NOT regenerated, until Enter or the confirm button commits both bounds
// and the derived label in one atomic update. This keeps the visible
// answer text and the numbers used by the backend's pack-matching
// (wellness_quiz.service.ts) from drifting apart, while still leaving the
// label freely editable afterward for custom wording.
function CalorieRangeField({ item, onConfirm }) {
    const [pendingMin, setPendingMin] = useState(item.minCalorie ?? null);
    const [pendingMax, setPendingMax] = useState(item.maxCalorie ?? null);

    const confirm = () => onConfirm(pendingMin, pendingMax);
    const handleKeyDown = (e) => {
        if (e.key === "Enter") confirm();
    };

    return (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <InputNumber
                size="small"
                placeholder="Min"
                value={pendingMin}
                onChange={setPendingMin}
                onKeyDown={handleKeyDown}
                style={{ width: 70, fontSize: 11 }}
            />
            <InputNumber
                size="small"
                placeholder="Max"
                value={pendingMax}
                onChange={setPendingMax}
                onKeyDown={handleKeyDown}
                style={{ width: 70, fontSize: 11 }}
            />
            <Tooltip title="Apply and update answer text">
                <Button
                    size="small"
                    icon={<CheckOutlined />}
                    onClick={confirm}
                    style={{ fontSize: 10, padding: "0 6px", height: 20, flexShrink: 0 }}
                />
            </Tooltip>
        </div>
    );
}

// Inline editor for a nested sub-list field within a CardsEditor item — a
// generic mechanism for any item field whose own value is itself a
// repeatable list of objects (e.g. quiz's answers[] inside a question card).
// Renders one row per sub-item (one control per sub-field, dispatched by
// `type` — plain/translatable text, icon picker, or async-fetched select)
// plus an inline remove button and a compact "+ Add" link. Fits inside
// CardsEditor's existing per-field column (confirmed by reading the actual
// layout: only one card expands at a time into an 8px/10px-padded column —
// this sits inline in that same space, not a separate panel/modal).
function NestedListEditor({ items, subFields, subNewItemDefault, onChange, selectedLanguage = "en" }) {
    const updateItem = (index, field, val) => {
        onChange(items.map((it, i) => (i === index ? { ...it, [field]: val } : it)));
    };

    // Same legacy-string-to-{en: value} upgrade rule as CardsEditor's own
    // updateTranslatableCard — a sub-item field can be translatable too.
    const updateTranslatableItem = (index, field, val) => {
        onChange(items.map((it, i) => {
            if (i !== index) return it;
            const existing = it[field];
            const asObject = existing == null || typeof existing === "string"
                ? (existing ? { en: existing } : {})
                : existing;
            return { ...it, [field]: { ...asObject, [selectedLanguage]: val } };
        }));
    };

    // Same id-generation rule as CardsEditor.addCard — a subNewItemDefault
    // with `id: null` (quiz's answers, cross-referenced by id from the
    // backend's answers map) gets a fresh unique id instead of literally
    // null, which would collide across every newly-added sub-item.
    const addItem = () => {
        const id = subNewItemDefault?.id === null ? crypto.randomUUID() : undefined;
        onChange([...items, { ...subNewItemDefault, ...(id ? { id } : {}) }]);
    };
    const removeItem = (index) => onChange(items.filter((_, i) => i !== index));

    // Writes minCalorie/maxCalorie and a regenerated label.en in one atomic
    // update (rather than three sequential updateItem calls, which would
    // each independently call onChange and could interleave oddly across
    // renders). Only label.en is overwritten — other languages are left
    // untouched, same as every other translatable field only ever writing
    // its currently-selected language.
    const confirmCalorieRange = (index, min, max) => {
        onChange(items.map((it, i) => {
            if (i !== index) return it;
            const existingLabel = it.label;
            const asObject = existingLabel == null || typeof existingLabel === "string"
                ? (existingLabel ? { en: existingLabel } : {})
                : existingLabel;
            return {
                ...it,
                minCalorie: min,
                maxCalorie: max,
                label: { ...asObject, en: formatCalorieRangeLabel(min, max) },
            };
        }));
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingLeft: 8, borderLeft: "2px solid #eee" }}>
            {items.map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                    {subFields.map(({ field, label, translatable, type, fetchOptions }) => {
                        if (type === "icon") {
                            return (
                                <IconPickerField
                                    key={field}
                                    value={item[field]}
                                    onChange={(name) => updateItem(i, field, name)}
                                />
                            );
                        }
                        if (type === "asyncSelect") {
                            return (
                                <AsyncSelectField
                                    key={field}
                                    value={item[field]}
                                    onChange={(val) => updateItem(i, field, val)}
                                    fetchOptions={fetchOptions}
                                    placeholder={label}
                                    style={{ minWidth: 140, fontSize: 11 }}
                                    size="small"
                                />
                            );
                        }
                        if (type === "number") {
                            return (
                                <InputNumber
                                    key={field}
                                    size="small"
                                    placeholder={label}
                                    value={item[field] ?? null}
                                    onChange={(val) => updateItem(i, field, val)}
                                    style={{ width: 90, fontSize: 11 }}
                                />
                            );
                        }
                        if (type === "calorieRange") {
                            return (
                                <CalorieRangeField
                                    key={field}
                                    item={item}
                                    onConfirm={(min, max) => confirmCalorieRange(i, min, max)}
                                />
                            );
                        }
                        return (
                            <Input
                                key={field}
                                size="small"
                                placeholder={label}
                                value={translatable ? getEditableTranslatableValue(item[field], selectedLanguage) : (item[field] || "")}
                                onChange={(e) => (translatable
                                    ? updateTranslatableItem(i, field, e.target.value)
                                    : updateItem(i, field, e.target.value))}
                                dir={translatable && selectedLanguage === "ar" ? "rtl" : "ltr"}
                                style={{ fontSize: 11 }}
                            />
                        );
                    })}
                    <Button
                        size="small"
                        danger
                        onClick={() => removeItem(i)}
                        style={{ fontSize: 10, padding: "0 6px", height: 20, flexShrink: 0 }}
                    >✕</Button>
                </div>
            ))}
            <Button size="small" onClick={addItem} style={{ fontSize: 11, alignSelf: "flex-start" }}>+ Add</Button>
        </div>
    );
}

export { NestedListEditor };
